import algoliasearch from 'algoliasearch/lite'

;(function () {
  'use strict'

  // Get defaults from CampsUtils
  const defaults = window.CampsUtils?.defaults || {}

  const CampsNearYouHome = {
    // Configuration (uses CampsUtils.defaults)
    config: {
      radius: defaults.radius,
      displayCount: defaults.displayCount,
      /** Fetch pool size from Algolia to find enough camps with sessions */
      fetchPoolSize: (defaults.displayCount || 8) * 5,
      maxDistanceForDisplay: defaults.maxDistanceForDisplay,
      algoliaAppId: defaults.algoliaAppId,
      algoliaSearchKey: defaults.algoliaSearchKey,
      algoliaIndex: defaults.algoliaIndex,
      cacheKey: defaults.cacheKey,
      cacheDuration: defaults.cacheDuration,
    },

    // DOM elements
    elements: {
      container: null,
      list: null,
      title: null,
      description: null,
      titleText: null,
      titleNear: null,
      cityZipPrefix: null,
      cityZip: null,
      viewAllLink: null,
    },

    // Camp codes from data attributes (curated selections from Craft)
    europeCampCodes: [],
    nonEuropeCampCodes: [],

    // Algolia index
    index: null,

    /**
     * Initialize the component
     */
    init: function () {
      this.elements.container = document.querySelector(
        '.rd-camps-near-you[data-page="home"]',
      )
      if (!this.elements.container) return

      // Get curated camp codes from data attributes
      try {
        this.europeCampCodes = JSON.parse(
          this.elements.container.dataset.europeCampCodes || '[]',
        )
      } catch (e) {
        this.europeCampCodes = []
      }
      try {
        this.nonEuropeCampCodes = JSON.parse(
          this.elements.container.dataset.nonEuropeCampCodes || '[]',
        )
      } catch (e) {
        this.nonEuropeCampCodes = []
      }

      this.elements.list = this.elements.container.querySelector(
        '.rd-camps-near-you__list',
      )
      this.elements.title = this.elements.container.querySelector(
        '.rd-camps-near-you__title',
      )
      this.elements.description = this.elements.container.querySelector(
        '.rd-camps-near-you__description',
      )
      this.elements.titleText =
        this.elements.container.querySelector('[data-title-text]')
      this.elements.titleNear =
        this.elements.container.querySelector('[data-title-near]')
      this.elements.cityZipPrefix = this.elements.container.querySelector(
        '[data-city-zip-prefix]',
      )
      this.elements.cityZip =
        this.elements.container.querySelector('[data-city-zip]')
      this.elements.viewAllLink = this.elements.container.querySelector(
        '.rd-camps-near-you__view-all',
      )

      this.observeAndLoad()
    },

    /**
     * Defer Algolia request until the section enters the viewport.
     * Saves Algolia operations for users who never scroll to this block.
     */
    observeAndLoad: function () {
      if (!('IntersectionObserver' in window)) {
        this.waitForAlgoliaAndLoad()
        return
      }

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              observer.disconnect()
              this.waitForAlgoliaAndLoad()
              break
            }
          }
        },
        { rootMargin: '200px 0px', threshold: 0 },
      )

      observer.observe(this.elements.container)
    },

    /**
     * Wait for Algolia library to load, then initialize
     */
    waitForAlgoliaAndLoad: function () {
      const maxAttempts = 50 // 5 seconds max
      let attempts = 0

      const checkAlgolia = () => {
        if (typeof algoliasearch !== 'undefined') {
          const client = algoliasearch(
            this.config.algoliaAppId,
            this.config.algoliaSearchKey,
          )
          this.index = client.initIndex(this.config.algoliaIndex)
          this.loadCamps()
        } else if (attempts < maxAttempts) {
          attempts++
          setTimeout(checkAlgolia, 100)
        } else {
          console.error(
            'CampsNearYouHome: Algolia library failed to load after 5 seconds',
          )
        }
      }

      checkAlgolia()
    },

    skeletonToggle: function (show) {
      if (this.elements.title) {
        this.elements.title.classList.toggle('rd-skeleton', show)
      }
      if (this.elements.description) {
        this.elements.description.classList.toggle('rd-skeleton', show)
      }
    },

    /**
     * Main function to load camps based on user location
     *
     * Logic:
     * 1. Detect user location
     * 2. US user: Fetch geo-sorted camps from Algolia
     * 3. Europe user: Fetch Europe camp codes from Algolia
     * 4. Other international: Fetch Non-Europe camp codes from Algolia
     * All paths render into the single list
     */
    loadCamps: async function () {
      let campsToRender = []
      let showDistanceFlag = false
      try {
        const location = await window.CampsUtils?.getUserLocationByIP({
          cacheKey: this.config.cacheKey,
          cacheDuration: this.config.cacheDuration,
        })

        this.updateViewAllLink(location)

        if (location.isUS) {
          console.log('[CampsNearYouHome] FLOW: US user detected')

          const hits = await this.fetchCampsUS(location)

          if (hits.length > 0) {
            console.log(
              `[CampsNearYouHome] FLOW: US → Found ${hits.length} camps within ${this.config.radius} miles`,
            )

            campsToRender =
              window.CampsUtils?.selectCampsWithSessionsPriority(
                hits,
                this.config.displayCount,
              ) || []
            showDistanceFlag = true
          } else {
            console.log(
              '[CampsNearYouHome] FLOW: US → No camps in radius → Fetching overnight fallback',
            )
            const overnightHits = await this.fetchOvernightFallback(location)
            console.log(
              `[CampsNearYouHome] FLOW: US → Overnight fallback → Found ${overnightHits.length} camps`,
            )

            campsToRender =
              window.CampsUtils?.selectCampsWithSessionsPriority(
                overnightHits,
                this.config.displayCount,
              ) || []
            showDistanceFlag = true
          }

          this.updateTitleForUS(location)
        } else if (location.inEurope) {
          console.log(
            '[CampsNearYouHome] FLOW: Europe user detected (inEurope=true)',
          )

          if (this.europeCampCodes.length > 0) {
            const europeCamps = await window.CampsUtils?.fetchCampsByCodes(
              this.index,
              this.europeCampCodes,
            )
            if (europeCamps && europeCamps.length > 0) {
              campsToRender = europeCamps
              showDistanceFlag = false
              console.log(
                `[CampsNearYouHome] FLOW: Rendered ${europeCamps.length} Europe curated camps`,
              )
            }
          }

          this.updateTitleForEurope()
        } else {
          console.log(
            '[CampsNearYouHome] FLOW: Non-US, Non-Europe user detected',
          )

          if (this.nonEuropeCampCodes.length > 0) {
            const nonEuropeCamps = await window.CampsUtils?.fetchCampsByCodes(
              this.index,
              this.nonEuropeCampCodes,
            )
            if (nonEuropeCamps && nonEuropeCamps.length > 0) {
              campsToRender = nonEuropeCamps
              showDistanceFlag = false
              console.log(
                `[CampsNearYouHome] FLOW: Rendered ${nonEuropeCamps.length} Non-Europe curated camps`,
              )
            }
          }

          this.updateTitleForNonEurope()
        }
      } catch (error) {
        console.error('[CampsNearYouHome] ERROR:', error)
      } finally {
        campsToRender.length > 0 &&
          this.renderCamps(campsToRender, showDistanceFlag)
        this.skeletonToggle(false)
      }
    },

    /**
     * Update title for Europe users
     */
    updateTitleForEurope: function () {
      if (this.elements.titleText) {
        this.elements.titleText.textContent = 'popular Camps in Europe'
        this.elements.titleText.classList.remove('hidden')
      }
      // Hide US-specific title elements
      if (this.elements.titleNear) {
        this.elements.titleNear.classList.add('hidden')
      }
      if (this.elements.cityZipPrefix) {
        this.elements.cityZipPrefix.classList.add('hidden')
      }
      if (this.elements.cityZip) {
        this.elements.cityZip.classList.add('hidden')
      }
    },

    /**
     * Update title for Non-Europe international users
     */
    updateTitleForNonEurope: function () {
      if (this.elements.titleText) {
        this.elements.titleText.textContent =
          'popular Camps in the UNITED STATES'
        this.elements.titleText.classList.remove('hidden')
      }
      // Hide US-specific title elements
      if (this.elements.titleNear) {
        this.elements.titleNear.classList.add('hidden')
      }
      if (this.elements.cityZipPrefix) {
        this.elements.cityZipPrefix.classList.add('hidden')
      }
      if (this.elements.cityZip) {
        this.elements.cityZip.classList.add('hidden')
      }
    },

    /**
     * Update title for US users (show "popular Camps near City, ST")
     */
    updateTitleForUS: function (location) {
      // Hide default title text
      if (this.elements.titleText) {
        this.elements.titleText.classList.add('hidden')
      }

      // Show US-specific title elements
      if (this.elements.titleNear) {
        this.elements.titleNear.classList.remove('hidden')
      }
      if (this.elements.cityZipPrefix) {
        this.elements.cityZipPrefix.textContent = 'near'
        this.elements.cityZipPrefix.classList.remove('hidden')
      }
      if (this.elements.cityZip) {
        const locationText =
          location.userLocation ||
          (location.city && location.regionCode
            ? `${location.city}, ${location.regionCode}`
            : location.city || '')
        this.elements.cityZip.textContent = locationText
        this.elements.cityZip.href =
          this.elements.viewAllLink?.href || '/search/results'
        this.elements.cityZip.classList.remove('hidden')
      }
    },

    /**
     * Fetch camps for US users within radius
     */
    fetchCampsUS: async function (coords) {
      const { hits } = await this.index.search('', {
        aroundLatLng: `${coords.lat},${coords.lng}`,
        aroundRadius: this.config.radius * 1609, // miles to meters
        hitsPerPage: this.config.fetchPoolSize,
        getRankingInfo: true,
      })
      return hits
    },

    /**
     * Fallback: fetch overnight camps without radius limit
     * Algolia will return camps sorted by distance
     */
    fetchOvernightFallback: async function (coords) {
      const { hits } = await this.index.search('', {
        aroundLatLng: `${coords.lat},${coords.lng}`,
        // No aroundRadius - Algolia returns sorted by distance
        hitsPerPage: this.config.fetchPoolSize,
        filters: 'feeCodes:"overnight"',
        getRankingInfo: true,
      })
      return hits
    },

    /**
     * Render camp cards into the single list
     * @param {Array} camps - Array of camp hits from Algolia
     * @param {boolean} showDistanceFlag - Whether distance should be shown
     */
    renderCamps: function (camps, showDistanceFlag) {
      if (!this.elements.list) return

      if (camps.length === 0) {
        this.handleNoCamps()
        return
      }

      const html = camps
        .map((camp) => {
          // Show distance whenever Algolia returned a number (incl. 0.0 mi);
          // hide only when geo data is absent.
          const showDistance =
            showDistanceFlag &&
            typeof camp._rankingInfo?.geoDistance === 'number'
          const showSessions = true
          return window.CampsUtils?.renderCampCardHtml(camp, {
            showDistance,
            showSessions,
          })
        })
        .join('')

      this.elements.list.innerHTML = html

      // Push GTM event
      pushGTMViewItemList(camps, false, null)
      selectItemTracking(camps, null)
    },

    /**
     * Handle case when no camps found
     */
    handleNoCamps: function () {
      if (this.elements.list) {
        this.elements.list.innerHTML =
          '<li class="rd-camps-near-you__no-results">No camps found.</li>'
      }
    },

    /**
     * Update "View All" link with location parameter
     */
    updateViewAllLink: function (coords) {
      if (!this.elements.viewAllLink) return

      const baseUrl = '/search/results'

      // For non-US users, link to results with no parameters
      if (!coords.isUS) {
        this.elements.viewAllLink.href = baseUrl
        return
      }

      // For US users, use userLocation if available, else city/regionCode
      const params = new URLSearchParams()
      const locationParam =
        coords.userLocation || `${coords.city}, ${coords.regionCode}`

      if (locationParam) {
        params.set('q', locationParam)
      }

      this.elements.viewAllLink.href = `${baseUrl}?${params.toString()}`
    },
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CampsNearYouHome.init())
  } else {
    CampsNearYouHome.init()
  }
})()
